import { RefObject } from 'react';
import ExcelJS from 'exceljs';
import html2canvas from "html2canvas";
import i18next from "i18next";
import { formatNumber } from './formatValue';
import { ICostDetail, ITimeDetail } from '../types';
import { createEmployeeTable, columnNumberToLetter } from "./table";

interface CostHourData {
  id: number;
  name: string;
  color: string;
  value: number;
}

export interface IAnalyticsExportData {
  name: string
  type: "projects" | "employees" | "categories"
  emplo_name: string
  report_period: string
  hoursImage: RefObject<HTMLDivElement>
  costImage: RefObject<HTMLDivElement>
  cost: CostHourData[] | null
  hours: CostHourData[] | null
  timeDetails: ITimeDetail[]
  costDetails: ICostDetail[]
}

const accentColor = "EF3129", fontName = "Calibri";

export const handleDownloadAnalyticsData = async (data: IAnalyticsExportData, setPopup?: (args: {popup: string, data: string}) => void) => {
  
  // Основные переменные
  const workbook = new ExcelJS.Workbook(), 
        mainName = data.name, type = data.type, 
        { t } = i18next,
        tr = (field: string) => t("analytics_export." + field);

  // Создание страницы Hours
  const worksheet = workbook.addWorksheet('Hours');

  // Функция для написания заголовка
  const createHeader = (worksheet: ExcelJS.Worksheet, page: string) => {
    worksheet.mergeCells('B2:H2');
    worksheet.getCell('B2').value = `${t(`analytics_export.${type === "projects" ? "projects_title" : "employees_title"}`)} (${page})`;
    worksheet.getCell('B2').font = { name: fontName, size: 22, bold: true };
    worksheet.getCell('B2').alignment = { horizontal: "left", vertical: 'middle' };
  };

  // Функция для написания параметров страницы (имя и период)
  const createField = (worksheet: ExcelJS.Worksheet, label: string, value: string, row: number) => {
    worksheet.getColumn("C").width = 15;

    //worksheet.mergeCells(`B${row}:C${row}`);
    worksheet.getCell(`B${row}`).value = label;
    worksheet.getCell(`B${row}`).font = { name: fontName, size: 14, bold: true };
    worksheet.getCell(`B${row}`).alignment = { vertical: 'middle' };

    worksheet.getCell(`D${row}`).value = value;
    worksheet.getCell(`D${row}`).font = { name: fontName, size: 12 };
    worksheet.getCell(`D${row}`).alignment = { vertical: 'middle' };
  };

  // Функции для создания изображения графика
  const addImageToSheet = async (worksheet: ExcelJS.Worksheet, imageRef: React.RefObject<HTMLDivElement>) => {
    if (imageRef.current) {
      const imageUrl = await captureImage(imageRef.current);
      const imageBuffer = await fetch(imageUrl).then(res => res.arrayBuffer());
      const imageId = workbook.addImage({
        buffer: imageBuffer,
        extension: 'png',
      });
      worksheet.addImage(imageId, `B7:G2${type === "projects" ? "7" : "6"}`);
    }
  };

  const captureImage = async (element: HTMLElement): Promise<string> => {
    const canvas = await html2canvas(element, {
      scale: 4
    });
    return canvas.toDataURL('image/png');
  };

  // Формирования мета-данных файла
  workbook.creator = 'IC-App';
  workbook.lastModifiedBy = 'IC-App';
  workbook.properties.date1904 = false;

  // Записывания данных в странице Hours
  createHeader(worksheet, tr("Hours"));
  createField(worksheet, tr("name") + ":", data.name, 4);
  createField(worksheet, tr("report_period") + ":", data.report_period, 5);

  await addImageToSheet(worksheet, data.hoursImage);

  // Функция для создания обводки
  function setCellBorder(worksheet: ExcelJS.Worksheet, cellAddress: string | number, cellLastIndex?: number) {
    if (cellLastIndex) {
      worksheet.getCell(cellAddress, cellLastIndex).border = {
        top: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
      };
    } else {
      worksheet.getCell(cellAddress).border = {
        top: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
      };
    }
  }

  // Функция для создания обводки (точками)
  function setCellDottedBorder(worksheet: ExcelJS.Worksheet, cellAddress: string | number, cellLastIndex?: number) {
    const style = "hair";
    if (cellLastIndex) {
      worksheet.getCell(cellAddress, cellLastIndex).border = {
        top: { style, color: { argb: '000000' } },
        left: { style, color: { argb: '000000' } },
        bottom: { style, color: { argb: '000000' } },
        right: { style, color: { argb: '000000' } },
      };
    } else {
      worksheet.getCell(cellAddress).border = {
        top: { style, color: { argb: '000000' } },
        left: { style, color: { argb: '000000' } },
        bottom: { style, color: { argb: '000000' } },
        right: { style, color: { argb: '000000' } },
      };
    }
  }

  // Функция для создания таблицы
  const createTable = (worksheet: ExcelJS.Worksheet, data: CostHourData[] | null, page: 'Hours' | 'Costs') => {

    const formatToFixed = page === "Hours" ? 1 : 2;

    // Объединения ячеек
    worksheet.mergeCells('B4:C4');
    worksheet.mergeCells('B5:C5');
    worksheet.mergeCells('D4:H4');
    worksheet.mergeCells('D5:H5');

    if (data) {
      const startRow = 7, startCell = 9, lastCell = type === "employees" ? startCell + 2 + data.length : startCell + 3;

      // Задавания размера колонок
      worksheet.getColumn('I').width = 5;
      worksheet.getColumn('J').width = 22.5;

      // Шапка таблицы
      worksheet.mergeCells('I7:J7');
      worksheet.getCell('I7').value = 'iC Ukraine Internal';
      worksheet.getCell('I7').font = { name: fontName, size: 10, family: 0, color: { argb: "FFFFFF" }, bold: true };
      worksheet.getCell('I7').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('I7').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
      setCellBorder(worksheet, 'I7:J7');

      worksheet.getCell('I8').value = 'No';
      worksheet.getCell('I8').font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
      worksheet.getCell('I8').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('I8').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
      setCellBorder(worksheet, 'I8');

      worksheet.getCell('J8').value = tr("users");
      worksheet.getCell('J8').font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
      worksheet.getCell('J8').alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell('J8').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
      setCellBorder(worksheet, 'J8');

      // Шапка таблицы (для работника)
      if (type === "employees") {
        data.forEach((item, index) => {
          const projectName = item.name.split("_")[0];
          worksheet.getCell(startRow, startCell + 2 + index).value = projectName;
          worksheet.getCell(startRow, startCell + 2 + index).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
          worksheet.getCell(startRow, startCell + 2 + index).alignment = { horizontal: 'center', vertical: 'middle' };
          worksheet.getCell(startRow, startCell + 2 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color.slice(1) } };
          setCellBorder(worksheet, startRow, startCell + 2 + index);

          worksheet.getCell(startRow + 1, startCell + 2 + index).value = item.name.slice(projectName.length + 1, item.name.length);
          worksheet.getCell(startRow + 1, startCell + 2 + index).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
          worksheet.getCell(startRow + 1, startCell + 2 + index).alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
          worksheet.getCell(startRow + 1, startCell + 2 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color.slice(1) } };
          worksheet.getColumn(startCell + 2 + index).width = 11;
          setCellBorder(worksheet, startRow + 1, startCell + 2 + index);
        })

        worksheet.getColumn(lastCell).width = 15;
        worksheet.getCell(startRow, lastCell).value = page === "Hours" ? tr("total_hours") : tr("total_cost");
        worksheet.getCell(startRow, lastCell).font = { name: fontName, size: 10, color: { argb: "000000" }, bold: true };
        worksheet.getCell(startRow, lastCell).alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
        worksheet.getCell(startRow, lastCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "c7c7c7" } };
        worksheet.mergeCells([startRow, lastCell, startRow + 1, lastCell]);
        setCellBorder(worksheet, startRow, lastCell);

      } else {
        // Шапка таблицы (для проекта)
        worksheet.getCell(startRow, startCell + 2).value = tr(page);
        worksheet.getCell(startRow, startCell + 2).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
        worksheet.getCell(startRow, startCell + 2).alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getCell(startRow, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
        worksheet.getColumn(startCell + 2).width = 10;
        worksheet.mergeCells([startRow, startCell + 2, startRow + 1, startCell + 2]);
        setCellBorder(worksheet, startRow, startCell + 2);
      }

      // Тело таблицы (для работника)
      if (type === "employees") {
        worksheet.getCell(startRow + 2, startCell).value = 1;
        worksheet.getCell(startRow + 2, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getCell(startRow + 2, startCell).font = { name: fontName, size: 10 };
        worksheet.getCell(startRow + 2, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
        worksheet.getCell(startRow + 2, startCell).border = {
          top: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
        };

        worksheet.getCell(startRow + 2, startCell + 1).value = mainName;
        worksheet.getCell(startRow + 2, startCell + 1).alignment = { wrapText: true, vertical: 'middle' };
        worksheet.getCell(startRow + 2, startCell + 1).font = { name: fontName, size: 10 };
        worksheet.getCell(startRow + 2, startCell + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
        worksheet.getColumn(startCell + 1).width = mainName.length + 1;
        worksheet.getCell(startRow + 2, startCell + 1).border = {
          top: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
        };

        let totalValue = 0;

        data.forEach((item, index) => {
          worksheet.getCell(startRow + 2, startCell + 2 + index).value = Number(formatNumber(item.value, formatToFixed));
          worksheet.getCell(startRow + 2, startCell + 2 + index).numFmt = `0.${"0".repeat(formatToFixed)}`;
          worksheet.getCell(startRow + 2, startCell + 2 + index).font = { name: fontName, size: 10 };
          worksheet.getCell(startRow + 2, startCell + 2 + index).alignment = { wrapText: true, vertical: 'middle', horizontal: "center" };
          worksheet.getCell(startRow + 2, startCell + 2 + index).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "FFFFFF" } };
          worksheet.getCell(startRow + 2, startCell + 2 + index).border = {
            top: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
          };

          totalValue += item.value;
        })

        
        const startCellLetter = columnNumberToLetter(startCell + 2),
        endCellLetter = columnNumberToLetter(lastCell-1);
        worksheet.getCell(startRow + 2, lastCell).value = { formula: `SUM(${startCellLetter}${startRow + 2}:${endCellLetter}${startRow + 2})`, result: String(totalValue), date1904: false };
        worksheet.getCell(startRow + 2, lastCell).numFmt = `0.${"0".repeat(formatToFixed)}`;
        worksheet.getCell(startRow + 2, lastCell).font = { name: fontName, size: 10, color: { argb: "000000" }, bold: true };
        worksheet.getCell(startRow + 2, lastCell).alignment = { vertical: 'middle', horizontal: "center" };
        worksheet.getCell(startRow + 2, lastCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "c7c7c7" } };
        worksheet.getCell(startRow + 2, lastCell).border = {
          top: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
        };

      } else {
        // Тело таблицы (для проекта)

        data.forEach((row, idx) => {
          const rowIdx = startRow + 2 + idx;

          worksheet.getCell(`I${rowIdx}`).value = row.id;
          worksheet.getCell(`I${rowIdx}`).alignment = { horizontal: 'center', vertical: 'middle' };
          worksheet.getCell(`I${rowIdx}`).font = { name: fontName, size: 10, color: { argb: "FFFFFF" } };
          worksheet.getCell(`I${rowIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: row.color.slice(1) } };
          worksheet.getCell(`I${rowIdx}`).border = {
            bottom: { style: 'hair', color: { argb: '000000' } },
            right: { style: 'hair', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
          };

          worksheet.getCell(`J${rowIdx}`).value = row.name;
          worksheet.getCell(`J${rowIdx}`).font = { name: fontName, size: 10 };
          worksheet.getCell(`J${rowIdx}`).alignment = { wrapText: true, vertical: 'middle' };
          worksheet.getCell(`J${rowIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
          worksheet.getCell(`J${rowIdx}`).border = {
            bottom: { style: 'hair', color: { argb: '000000' } },
            right: { style: 'hair', color: { argb: '000000' } },
          };

          worksheet.getCell(`K${rowIdx}`).value = Number(formatNumber(row.value, formatToFixed));
          worksheet.getCell(`K${rowIdx}`).numFmt = `0.${"0".repeat(formatToFixed)}`;
          worksheet.getCell(`K${rowIdx}`).font = { name: fontName, size: 10 };
          worksheet.getCell(`K${rowIdx}`).alignment = { horizontal: "center", vertical: 'middle' };
          worksheet.getCell(`K${rowIdx}`).border = {
            bottom: { style: 'hair', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
          };
        });

        // Подвал таблицы (total)
        worksheet.mergeCells([startRow + 2 + data.length, startCell, startRow + 2 + data.length, startCell + 1]);
        const lastRow = startRow + 2 + data.length;

        worksheet.getCell(lastRow, startCell).value = page === "Hours" ? tr("total_hours") : tr("total_cost");
        worksheet.getCell(lastRow, startCell).font = { name: fontName, size: 10, bold: true };
        worksheet.getCell(lastRow, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c7c7c7' } };
        worksheet.getCell(lastRow, startCell).alignment = { horizontal: 'center', vertical: 'middle' };

        const totalValue = data.reduce((sum, item) => sum + item.value, 0);
        worksheet.getCell(lastRow, startCell + 2).value = { formula: `SUM(K${startRow + 2}:K${lastRow - 1})`, result: String(totalValue), date1904: false };
        worksheet.getCell(lastRow, startCell + 2).numFmt = `0.${"0".repeat(formatToFixed)}`;
        worksheet.getCell(lastRow, startCell + 2).font = { name: fontName, size: 10, bold: true };
        worksheet.getCell(lastRow, startCell + 2).alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getCell(lastRow, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c7c7c7' } };

        setCellBorder(worksheet, lastRow, startCell);
        setCellBorder(worksheet, lastRow, startCell + 2);
      }
    }
  };

  // Создание таблицы Hours
  createTable(worksheet, data.hours, "Hours");

  // Создание таблицы Costs
  const worksheetCoins = workbook.addWorksheet("Costs");
  createHeader(worksheetCoins, tr("Costs"));
  createField(worksheetCoins, tr("name") + ":", data.name, 4);
  createField(worksheetCoins, tr("report_period") + ":", data.report_period, 5);

  await addImageToSheet(worksheetCoins, data.costImage);

  createTable(worksheetCoins, data.cost, "Costs");

  // Формирования данных для таблиц по работнику
  const employees: string[] = [];
  if(data.type === "employees") {
    employees.push(data.name);
  } else {
      data.hours && data.hours.forEach(item => {
        if(!employees.includes(item.name)) {
          employees.push(item.name);
        }
      });

      data.cost && data.cost.forEach(item => {
        if(!employees.includes(item.name)) {
          employees.push(item.name);
        }
      })
  }

  employees.forEach(name => {
    // Создание страницы с работником и записывания данных в ней
    const worksheet = workbook.addWorksheet(name);
    worksheet.mergeCells([4, 4, 4, 6]);
    worksheet.mergeCells([4, 2, 4, 3]);
    worksheet.mergeCells([5, 4, 5, 6]);
    worksheet.mergeCells([5, 2, 5, 3]);
    createField(worksheet, `${t("employees_admin.table.employee")}:`, name, 4);
    createField(worksheet, `${tr("report_period")}:`, data.report_period, 5);
    worksheet.getColumn("C").width = 9;

    // Создание таблицы
    createEmployeeTable(worksheet, {
      time: data.timeDetails.filter(item => item.user_name === name),
      cost: data.costDetails.filter(item => item.user_name === name)
    })
  })

  // Скрипт для экспорта сгенерированного файла
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], { type: 'application/octet-stream' }),
      date = new Date(), year = String(date.getFullYear()).slice(2, 4), month = date.getMonth() + 1 > 9 ? String(date.getMonth() + 1) : `0${date.getMonth() + 1}`,
      day = date.getDate() > 9 ? String(date.getDate()) : `0${date.getDate()}`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${year}${month}${day}${type === "projects" ? "_Project" : "_Employee"}_Statistic_Report.xlsx`;
    link.click();
    link.remove();

    if(setPopup) setPopup({popup: "", data: ""});
  });
};